import FetchError from '@/shared/utils/api/FetchError';
import type { AuthContextProps } from '../Auth.types';
import AuthService from '../service/AuthService';
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStorage } from '../config/auth-storage';

interface UseTokenRefreshProps {
  state: AuthContextProps['state'];
  isLoggingOut: AuthContextProps['isLoggingOut'];
  setError: AuthContextProps['setError'];
  logout: AuthContextProps['logout'];
}

const useTokenRefresh = (props: UseTokenRefreshProps) => {
  const { state, isLoggingOut, logout } = props;
  const REFRESH_INTERVAL = parseInt(import.meta.env.VITE_DEV_JWT_REFRESH_INTERVAL, 10) || 1800000; // 30 minutes

  const [wakeLock, setWakeLock] = useState<WakeLockSentinel | null>(null);
  const [isMinimized, setIsMinimized] = useState(document.hidden);

  const isMinimizedRef = useRef(isMinimized);
  const refreshIntervalRef = useRef<number | null>(null);

  /** Token refresh handler */
  const handleRefreshTokens = useCallback(async () => {
    if (state !== 'loggedin') {
      return;
    }

    const refreshToken = authStorage.getItem('refresh_token');
    if (!refreshToken) {
      return;
    }
    // Don't refresh if the user is logging out (across tabs)
    if (isLoggingOut) {
      return;
    }

    try {
      const { access_token, refresh_token } = await AuthService.refreshTokens({ refreshToken });
      const { csrf_token } = await AuthService.CSRFToken();
      AuthService.setTokens({
        accessToken: access_token,
        refreshToken: refresh_token,
        csrfToken: csrf_token,
      });
    } catch (error) {
      if (error instanceof FetchError) {
        console.error('Error while refreshing tokens: ', error.error);
      }
      await logout({ redirectTo: '/auth/login' });
    }
  }, [isLoggingOut, logout, state]);

  /** Initialize timer handler */
  const handleStartRefreshInterval = useCallback(() => {
    refreshIntervalRef.current ??= window.setInterval(handleRefreshTokens, REFRESH_INTERVAL);
  }, [REFRESH_INTERVAL, handleRefreshTokens]);

  /** Clear timer handler */
  const handleStopRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  /** Acquire wake lock handler */
  const handleAcquireWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator && !isMinimizedRef.current && !wakeLock) {
      try {
        const newWakeLock = await navigator.wakeLock.request('screen');
        setWakeLock(newWakeLock);
      } catch (error) {
        console.error('Wake lock error: ', error);
      }
    }
  }, [wakeLock]);

  /** Detect visibility handler */
  const handleVisibilityChange = useCallback(() => {
    const isHidden = document.hidden;
    isMinimizedRef.current = isHidden;
    setIsMinimized(isHidden);
  }, []);

  /** Handle refresh interval start/stop */
  useEffect(() => {
    if (state !== 'loggedin') {
      handleStopRefreshInterval();
      if (wakeLock) {
        wakeLock.release();
        setWakeLock(null);
      }
      return;
    }

    handleStartRefreshInterval();
    if (!document.hidden) {
      handleAcquireWakeLock();
    }
  }, [
    state,
    handleAcquireWakeLock,
    handleStartRefreshInterval,
    handleStopRefreshInterval,
    wakeLock,
  ]);

  /** Handle visibility change */
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) wakeLock.release();
    };
  }, [handleVisibilityChange, wakeLock]);

  /** Handle cleanup on onmount */
  useEffect(() => {
    return () => {
      handleStopRefreshInterval();
    };
  }, [handleStopRefreshInterval]);
};

export default useTokenRefresh;
